import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";

export interface UseRequireTenantTypeResult {
  hasAccess: boolean | null; // true if allowed, false if denied, null if loading
  isLoading: boolean; // true if tenantType is not loaded yet
  isAllowed: boolean; // true only if hasAccess === true (convenience flag)
}

/**
 * Custom hook to restrict page access based on user's TenantType
 * 
 * @param allowedTypes - Array of TenantType values that are allowed to access the page
 * @param redirectTo - Path to redirect to if user doesn't have required type (default: "/")
 * @returns UseRequireTenantTypeResult - Object with access status flags
 * 
 * @example
 * // Only allow PARTNER and GLOBAL_ADMIN
 * const { hasAccess, isLoading, isAllowed } = useRequireTenantType(["PARTNER", "GLOBAL_ADMIN"]);
 * if (!isAllowed) return <Loader />;
 * 
 * @example
 * // In useEffect, check before making API calls
 * useEffect(() => {
 *   if (!isAllowed) return; // Don't make API call if access not confirmed
 *   // ... API call logic
 * }, [isAllowed, ...otherDeps]);
 */
export function useRequireTenantType(
  allowedTypes: string[],
  redirectTo = "/"
): UseRequireTenantTypeResult {
  const navigate = useNavigate();
  const { TenantType: tenantType } = useAppSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.userProfile
  );

  // Check if user has access (synchronously, before render)
  // Returns null if tenantType is not loaded yet (show loader), 
  // true if access is allowed, false if not allowed
  const hasAccess = useMemo(() => {
    if (!tenantType) return null; // tenantType not loaded yet - show loader
    return allowedTypes.includes(tenantType);
  }, [tenantType, allowedTypes]);

  const isLoading = tenantType === undefined || tenantType === "";

  useEffect(() => {
    // If tenantType is loaded and user doesn't have access, redirect
    if (tenantType && hasAccess === false) {
      navigate(redirectTo, { replace: true });
    }
  }, [tenantType, hasAccess, redirectTo, navigate]);

  return {
    hasAccess,
    isLoading,
    isAllowed: hasAccess === true, // Only true when access is explicitly confirmed
  };
}
